import React from 'react';
import './leaderboard_report.scss';
import CommonApi from '../../../apis/common';
import SchoolApi from '../../../apis/school';
import SessionCache from '../../routes/session-cache';
import { getImageSource } from '../../../utility/image-source';
import { ImageConstants } from '../../../constants/constants';
import Dropdown from '../../common/dropdown-v2/dropdown';
import { jsPDF } from 'jspdf';
import logoWhite from '../../../assets/images/logo-white.png';

const formatMonthNavigatorLabel = (date) => {
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

const MonthNavigator = ({ currentMonthDate, onMonthChange, isCurrentMonth }) => {
    const label = formatMonthNavigatorLabel(currentMonthDate);
    return (
        <div className="month-navigator">
            <button className="nav-arrow prev" onClick={() => onMonthChange('prev')}>&larr;</button>
            <div className="month-label">{label}</div>
            <button
                className={`nav-arrow next ${isCurrentMonth ? 'disabled' : ''}`}
                onClick={() => onMonthChange('next')}
                disabled={isCurrentMonth}
            >
                &rarr;
            </button>
        </div>
    );
};

export default class LeaderboardReport extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentMonthDate: (() => {
                const today = new Date();
                return new Date(today.getFullYear(), today.getMonth(), 1);
            })(),
            reportData: [],
            isLoading: false,
            board: '',
            boardsDropdownItems: [],
            isPdfGenerating: false
        };
    }

    componentDidMount() {
        this.initializeSchoolData();
    }

    initializeSchoolData = async () => {
        let userInfo = SessionCache.getUser();
        if (!userInfo) return;

        try {
            const schoolInfo = await SchoolApi.getClassAndBoard({
                type: 'school',
                userId: userInfo._id || userInfo.userId
            });

            let classes = schoolInfo.classes ? schoolInfo.classes.map(c => c.name) : [];
            if (classes.length === 0) {
                classes = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
            }
            // Sort classes numerically
            classes.sort((a, b) => {
                const numA = parseInt(a.replace(/\D/g, ''), 10) || 0;
                const numB = parseInt(b.replace(/\D/g, ''), 10) || 0;
                return numA - numB;
            });

            let boards = [];
            if (schoolInfo.boards && schoolInfo.boards.length > 0) {
                boards = schoolInfo.boards.map(b => ({
                    title: b.name,
                    value: b.name
                }));
            }

            let defaultBoard = '';
            if (schoolInfo.boards && schoolInfo.boards.length > 0) {
                defaultBoard = userInfo.board ? userInfo.board : schoolInfo.boards[0].name;
            }

            this.setState({
                classes,
                boardsDropdownItems: boards,
                board: defaultBoard
            }, () => {
                this.fetchReportData();
            });
        } catch (e) {
            console.error("Error fetching school info", e);
        }
    }

    fetchReportData = async () => {
        this.setState({ isLoading: true, reportData: [] });

        let userInfo = SessionCache.getUser();
        if (!userInfo) {
            this.setState({ isLoading: false });
            return;
        }

        try {
            const { classes, board } = this.state;
            if (!classes || classes.length === 0) {
                this.setState({ isLoading: false });
                return;
            }

            const year = this.state.currentMonthDate.getFullYear();
            const monthStr = String(this.state.currentMonthDate.getMonth() + 1).padStart(2, '0');
            const apiMonth = `${year}-${monthStr}`;

            let reportData = [];
            for (const className of classes) {
                let classData = { className, sections: [] };

                // 1. Fetch sections for this class
                let sections = [];
                try {
                    let secRes = await SchoolApi.getSections({ class: className, board });
                    if (secRes && secRes.response && secRes.response.length > 0) {
                        sections = secRes.response
                            .filter(s => s.section !== 'No Sections')
                            .map(s => ({ title: s.section, id: s._id }));
                    }
                } catch (e) {
                    console.error("Error fetching sections for", className, e);
                }

                if (sections.length === 0) {
                    sections.push({ title: 'All', id: '' });
                }

                // 2. Fetch Leaderboard for each section
                for (const sec of sections) {
                    let secData = { sectionName: sec.title, sectionId: sec.id, topPerformer: null, selfLearningLeader: null };

                    let payloadTP = {
                        userId: userInfo.userId || userInfo._id,
                        category: 'Top Performer',
                        month: apiMonth,
                        subjectId: userInfo.subjectId || null,
                        selectedBoard: board,
                        selectedClass: className,
                        selectedSection: sec.id
                    };

                    try {
                        let res = await CommonApi.getStudentMeritLeaderboard(payloadTP);
                        if (res && res.status === 1 && res.data && res.data.length > 0) {
                            let formattedData = res.data.map(item => {
                                let merits = item.finalMerits ?? 0;
                                merits = item.totalActivityMeritsAtRank ?? item.totalActivityMerits ?? merits;
                                return {
                                    rank: item.rank ?? null,
                                    name: item.name || "Unknown Student",
                                    merits: merits,
                                    userId: item.userId
                                };
                            });
                            formattedData.sort((a, b) => {
                                if (!a.rank && !b.rank) return 0;
                                if (!a.rank) return 1;
                                if (!b.rank) return -1;
                                return Number(a.rank) - Number(b.rank);
                            });
                            let top3 = formattedData.filter(item => Number(item.rank) > 0 && Number(item.rank) <= 3);
                            let grouped = {};
                            top3.forEach(item => {
                                let r = Number(item.rank);
                                if (!grouped[r]) grouped[r] = { rank: r, students: [] };
                                grouped[r].students.push(item);
                            });
                            secData.topPerformers = Object.values(grouped).sort((a, b) => a.rank - b.rank);
                        }
                    } catch (e) {
                        console.error("Error fetching TP for", className, sec.title, e);
                    }

                    let payloadSLL = {
                        userId: userInfo.userId || userInfo._id,
                        category: 'Self Learning Leader',
                        month: apiMonth,
                        subjectId: userInfo.subjectId || null,
                        selectedBoard: board,
                        selectedClass: className,
                        selectedSection: sec.id
                    };

                    try {
                        let res = await CommonApi.getStudentMeritLeaderboard(payloadSLL);
                        if (res && res.status === 1 && res.data && res.data.length > 0) {
                            let formattedData = res.data.map(item => {
                                let merits = item.finalMerits ?? 0;
                                merits = item.selfLearningMeritsAtRank ?? item.selfLearningMerits ?? merits;
                                return {
                                    rank: item.rank ?? null,
                                    name: item.name || "Unknown Student",
                                    merits: merits,
                                    userId: item.userId
                                };
                            });
                            formattedData.sort((a, b) => {
                                if (!a.rank && !b.rank) return 0;
                                if (!a.rank) return 1;
                                if (!b.rank) return -1;
                                return Number(a.rank) - Number(b.rank);
                            });
                            let top3 = formattedData.filter(item => Number(item.rank) > 0 && Number(item.rank) <= 3);
                            let grouped = {};
                            top3.forEach(item => {
                                let r = Number(item.rank);
                                if (!grouped[r]) grouped[r] = { rank: r, students: [] };
                                grouped[r].students.push(item);
                            });
                            secData.selfLearningLeaders = Object.values(grouped).sort((a, b) => a.rank - b.rank);
                        }
                    } catch (e) {
                        console.error("Error fetching SLL for", className, sec.title, e);
                    }

                    if ((secData.topPerformers && secData.topPerformers.length > 0) || (secData.selfLearningLeaders && secData.selfLearningLeaders.length > 0)) {
                        classData.sections.push(secData);
                    }
                }

                if (classData.sections.length > 0) {
                    reportData.push(classData);
                    // Update state incrementally so user sees progress
                    this.setState({ reportData: [...reportData] });
                }
            }

            this.setState({ isLoading: false });

        } catch (e) {
            console.error("Error fetching report data", e);
            this.setState({ isLoading: false });
        }
    }

    handleMonthChange = (direction) => {
        const today = new Date();
        const currentSel = this.state.currentMonthDate;
        let newSel;

        if (direction === 'prev') {
            newSel = new Date(currentSel.getFullYear(), currentSel.getMonth() - 1, 1);
            this.setState({ currentMonthDate: newSel }, () => {
                this.fetchReportData();
            });
        } else if (direction === 'next') {
            newSel = new Date(currentSel.getFullYear(), currentSel.getMonth() + 1, 1);
            if (newSel <= new Date(today.getFullYear(), today.getMonth(), 1)) {
                this.setState({ currentMonthDate: newSel }, () => {
                    this.fetchReportData();
                });
            }
        }
    }

    downloadPDF = async () => {
        this.setState({ isPdfGenerating: true });
        try {
            const doc = new jsPDF({ orientation: 'landscape', format: 'a4', unit: 'pt' });
            await this.generatePDF(doc);
            
            let userInfo = SessionCache.getUser();
            let schoolName = userInfo?.schoolInfo?.name || userInfo?.schoolName || 'school';
            let formattedSchoolName = schoolName.replace(/\s+/g, '_');
            
            const d = new Date();
            const formattedDate = `${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, '0')}_${String(d.getDate()).padStart(2, '0')}`;
            
            const fileName = `merit_leaderboard_${formattedSchoolName}_${formattedDate}.pdf`;
            
            doc.save(fileName);
            
            this.setState({ isPdfGenerating: false });
        } catch (e) {
            console.error(e);
            alert("Error: PDF generation failed.");
            this.setState({ isPdfGenerating: false });
        }
    }

    getBase64ImageFromUrl = async (imageUrl) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve({
                    dataUrl: canvas.toDataURL('image/png'),
                    width: img.width,
                    height: img.height
                });
            };
            img.onerror = reject;
            img.src = imageUrl;
        });
    }

    generatePDF = async (doc) => {
        const W = 842;
        const H = 595;
        
        const PORTAL_NAVY = '#1a2e35';
        const PORTAL_NAVY_MID = '#243b44';
        const PORTAL_TEAL = '#19c2d1';
        const GOLD = '#F7C948';
        const SILVER = '#cbd5e1';
        const BRONZE = '#D4956A';
        const WHITE = '#ffffff';
        const TEXT_MUTED = '#94a3b8';

        let userInfo = SessionCache.getUser();
        let schoolName = userInfo?.schoolInfo?.name || userInfo?.schoolName || 'THE SCHOOL';

        let flattenedReportData = [];
        this.state.reportData.forEach(c => {
            const cleanClassName = c.className.toUpperCase().replace('CLASS ', '').trim();
            if (c.sections.length === 1 && c.sections[0].sectionName === 'All') {
                flattenedReportData.push({
                    className: `CLASS ${cleanClassName}`,
                    topPerformers: c.sections[0].topPerformers || [],
                    selfLearningLeaders: c.sections[0].selfLearningLeaders || []
                });
            } else {
                c.sections.forEach(sec => {
                    const cleanSecName = sec.sectionName.toUpperCase().replace('SECTION ', '').trim();
                    flattenedReportData.push({
                        className: `CLASS ${cleanClassName} - ${cleanSecName}`,
                        topPerformers: sec.topPerformers || [],
                        selfLearningLeaders: sec.selfLearningLeaders || []
                    });
                });
            }
        });

        if (flattenedReportData.length === 0) {
            flattenedReportData.push({
                className: 'ALL CLASSES',
                topPerformers: [],
                selfLearningLeaders: []
            });
        }

        const monthName = this.state.currentMonthDate.toLocaleString('default', { month: 'long' });
        const yearPDF = this.state.currentMonthDate.getFullYear();
        const monthYearStr = `${monthName.toUpperCase()} ${yearPDF}`;

        let logo = null;
        try {
            logo = await this.getBase64ImageFromUrl(logoWhite);
        } catch (err) {
            console.error("Failed to load logo for PDF", err);
        }

        flattenedReportData.forEach((pageData, pageIdx) => {
            if (pageIdx > 0) doc.addPage();

            // Background
            doc.setFillColor(PORTAL_NAVY);
            doc.rect(0, 0, W, H, 'F');

            // Page Border
            doc.setDrawColor(GOLD);
            doc.setLineWidth(1.5);
            doc.rect(15, 15, W - 30, H - 30, 'S');

            // Faint geometric lines radiating
            doc.setDrawColor(PORTAL_NAVY_MID);
            doc.setLineWidth(1);
            for (let i = 0; i < 12; i++) {
                doc.line(W/2, H + 200, W/2 + Math.cos(i * Math.PI/12)*1000, H + 200 - Math.sin(i * Math.PI/12)*1000);
            }

            // Background Stars (+)
            const drawStar = (sx, sy, color) => {
                doc.setDrawColor(color);
                doc.setLineWidth(1);
                doc.line(sx - 4, sy, sx + 4, sy);
                doc.line(sx, sy - 4, sx, sy + 4);
            };
            drawStar(80, 120, GOLD);
            drawStar(W - 80, 120, PORTAL_TEAL);
            drawStar(50, 450, BRONZE);
            drawStar(W - 60, 480, GOLD);

            if (logo) {
                const logoW = 110;
                const logoH = (logo.height * logoW) / logo.width;
                doc.addImage(logo.dataUrl, 'PNG', 36, 40, logoW, logoH);
            }

            // Title
            const titleX = W / 2;
            const titleY = 50;
            const titleText = 'MERIT CHAMPIONS';
            
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(26);
            doc.setTextColor(GOLD);
            doc.text(titleText, titleX, titleY, { align: 'center' });
                
            // Golden Ribbon
            const ribbonW = 480;
            const ribbonH = 22;
            const ribbonX = W/2 - ribbonW/2;
            const ribbonY = 65;
            
            doc.setFillColor(GOLD);
            doc.rect(ribbonX, ribbonY, ribbonW, ribbonH, 'F');
            
            doc.setFillColor('#b45309');
            doc.triangle(ribbonX, ribbonY + ribbonH, ribbonX, ribbonY + ribbonH + 8, ribbonX + 15, ribbonY + ribbonH, 'F');
            doc.triangle(ribbonX + ribbonW, ribbonY + ribbonH, ribbonX + ribbonW, ribbonY + ribbonH + 8, ribbonX + ribbonW - 15, ribbonY + ribbonH, 'F');
            
            doc.setFillColor(GOLD);
            doc.triangle(ribbonX, ribbonY, ribbonX, ribbonY + ribbonH, ribbonX - 20, ribbonY + ribbonH/2, 'F');
            doc.triangle(ribbonX + ribbonW, ribbonY, ribbonX + ribbonW, ribbonY + ribbonH, ribbonX + ribbonW + 20, ribbonY + ribbonH/2, 'F');

            doc.setFontSize(9);
            doc.setTextColor('#422006');
            let sName = schoolName.toUpperCase();
            if (sName.length > 40) sName = sName.substring(0, 37) + '...';
            const ribbonText = `${sName}   •   ${pageData.className.toUpperCase()}   •   ${monthYearStr}`;
            doc.text(ribbonText, W/2, ribbonY + 15, { align: 'center' });
            
            // Subtitle
            doc.setFontSize(10);
            doc.setTextColor(PORTAL_TEAL);
            doc.text('Celebrating Excellence', W/2, ribbonY + 40, { align: 'center' });

            // Category configuration
            const CATEGORIES = [
                {
                    type: 'TOP_PERFORMER',
                    title: 'TOP PERFORMER',
                    headerBg: '#0f2923',
                    accentColor: GOLD,
                    tagColor: '#ffffff',
                    tagBg: '#0f2923',
                    tagText: 'MONTHLY',
                    students: pageData.topPerformers || []
                },
                {
                    type: 'SELF_LEARNING_LEADER',
                    title: 'SELF LEARNING LEADER',
                    headerBg: '#5c2c06',
                    accentColor: BRONZE,
                    tagColor: '#ffffff',
                    tagBg: '#5c2c06',
                    tagText: 'MONTHLY',
                    students: pageData.selfLearningLeaders || []
                }
            ];

            const drawCategoryIcon = (cx, cy, type) => {
                doc.setFillColor('#ffffff');
                doc.circle(cx, cy, 12, 'F');
                doc.setDrawColor(type === 'TOP_PERFORMER' ? GOLD : BRONZE);
                doc.setLineWidth(1.5);
                doc.circle(cx, cy, 12, 'S');
                doc.setFillColor(type === 'TOP_PERFORMER' ? GOLD : BRONZE);
                if (type === 'TOP_PERFORMER') {
                    doc.triangle(cx, cy - 6, cx - 6, cy + 4, cx + 6, cy + 4, 'F');
                } else {
                    doc.roundedRect(cx - 5, cy - 4, 10, 3, 1, 1, 'F');
                    doc.roundedRect(cx - 5, cy + 1, 10, 3, 1, 1, 'F');
                }
            };

            const drawCategoryCard = (x, y, w, h, rowStep, cat) => {
                const headerH = 40;

                // 1. Draw white card background
                doc.setFillColor(WHITE);
                doc.roundedRect(x, y, w, h, 10, 10, 'F');
                
                // 2. Draw header background with rounded top corners
                doc.setFillColor(cat.headerBg);
                doc.roundedRect(x + 1, y + 1, w - 2, headerH, 10, 10, 'F'); // rounded top
                doc.rect(x + 1, y + 10, w - 2, headerH - 9, 'F'); // solid bottom half of header
                
                // 3. Draw thick outer border OVER everything to clip edges cleanly
                doc.setDrawColor(cat.type === 'TOP_PERFORMER' ? GOLD : BRONZE);
                doc.setLineWidth(2.5);
                doc.roundedRect(x, y, w, h, 10, 10, 'S');
                
                drawCategoryIcon(x + 22, y + headerH / 2, cat.type);
                
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(14);
                doc.setTextColor('#ffffff');
                doc.text(cat.title, x + 38, y + 25);
                   
                const tagW = 75, tagH = 20;
                const tagX = x + w - tagW - 16, tagY = y + (headerH - tagH) / 2;
                doc.setFillColor(GOLD);
                doc.roundedRect(tagX, tagY, tagW, tagH, 10, 10, 'F');
                doc.setFontSize(8);
                doc.setTextColor('#78350f');
                doc.text(cat.tagText, tagX + tagW / 2, tagY + 14, { align: 'center' });

                const sx = x + 16, sw = w - 32;
                let sy = y + headerH + 16;
                const bx = sx + 14; // Center of the medal column
                
                doc.setFontSize(8);
                doc.setTextColor('#94a3b8');
                doc.text('RANK', bx, sy, { align: 'center' });
                doc.text('STUDENT', sx + 50, sy);
                doc.text('MERITS', sx + sw - 10, sy, { align: 'right' });
                
                doc.setDrawColor('#94a3b8');
                doc.setLineWidth(1);
                doc.line(sx, sy + 14, sx + sw, sy + 14);

                sy += 24;

                const maxH = 219;
                const fSizeMed = calcDynamicFontSize(cat, rowStep, maxH);
                const lineHeight = fSizeMed + 3;

                if (!cat.students || cat.students.length === 0) {
                    doc.setFontSize(Math.max(6, fSizeMed - 1));
                    doc.setFont('helvetica', 'italic');
                    doc.setTextColor('#cbd5e1');
                    doc.text("- Keep learning to grab this spot -", x + w/2, y + headerH + (h - headerH)/2, { align: 'center' });
                    doc.setFont('helvetica', 'normal');
                }

                let currentRankIndex = 0;
                cat.students.forEach((sGroup, idx) => {

                    const studentCount = sGroup.students.length;
                    const requiredHeight = Math.max(rowStep, 10 + (studentCount * lineHeight));
                    
                    doc.setFillColor('#f8fafc');
                    doc.roundedRect(sx, sy, sw, requiredHeight - 4, 4, 4, 'F');
                    
                    // Calculate text start position first so badge can align mathematically to text block
                    const textStartY = sy + (requiredHeight - 4)/2 - ((studentCount - 1) * lineHeight) / 2 + (fSizeMed / 3);

                    // Rank Badge
                    let badgeColor = '#94a3b8'; // default grey
                    if (sGroup.rank === 1) badgeColor = GOLD;
                    else if (sGroup.rank === 2) badgeColor = '#cbd5e1'; // Silver-ish
                    else if (sGroup.rank === 3) badgeColor = BRONZE;

                    const bx = sx + 14;
                    const by = textStartY + ((studentCount - 1) * lineHeight) / 2; // center badge to match text block center
                    
                    doc.setFillColor(badgeColor);
                    doc.circle(bx, by, 10, 'F');
                    
                    // Tiny Crown for Top 3 Ranks
                    if (sGroup.rank <= 3) {
                        doc.setFillColor(badgeColor);
                        // base of crown
                        doc.rect(bx - 5, by - 11, 10, 3, 'F');
                        // spikes
                        doc.triangle(bx - 5, by - 11, bx - 2, by - 11, bx - 6, by - 16, 'F');
                        doc.triangle(bx - 2, by - 11, bx + 2, by - 11, bx, by - 17, 'F');
                        doc.triangle(bx + 2, by - 11, bx + 5, by - 11, bx + 6, by - 16, 'F');
                        // jewels
                        doc.circle(bx - 6, by - 16, 1.5, 'F');
                        doc.circle(bx, by - 17, 1.5, 'F');
                        doc.circle(bx + 6, by - 16, 1.5, 'F');
                    }

                    doc.setFontSize(fSizeMed);
                    doc.setTextColor(sGroup.rank === 2 ? '#475569' : '#ffffff');
                    doc.text((sGroup.rank || '-').toString(), bx, by + Math.max(3, fSizeMed/3), { align: 'center' });
                        
                    doc.setFontSize(fSizeMed);

                    sGroup.students.forEach((s, sIdx) => {
                         const studentY = textStartY + (sIdx * lineHeight);

                         doc.setTextColor('#334155');
                         let sname = s.name;
                         if (sname && sname.length > 25) sname = sname.substring(0, 22) + '...';
                         doc.text(sname || "Unknown", sx + 50, studentY);
                         
                         // Score
                         doc.setTextColor(cat.accentColor === GOLD ? '#b45309' : '#7c2d12');
                         doc.text(Number(s.merits).toFixed(2), sx + sw - 10, studentY, { align: 'right' });
                    });
                    
                    sy += requiredHeight;
                    currentRankIndex++;
                });
                
                // Empty state for remaining rows up to 3
                if (cat.students && cat.students.length > 0) {
                    for (let i = currentRankIndex; i < 3; i++) {
                        const eY = sy + (rowStep) / 2 + (fSizeMed / 3);
                        doc.setFontSize(Math.max(6, fSizeMed - 1));
                        doc.setFont('helvetica', 'italic');
                        doc.setTextColor('#cbd5e1'); // Very faint gray
                        doc.text("- Keep learning to grab this spot -", sx + sw / 2, eY, { align: 'center' });
                        doc.setFont('helvetica', 'normal');
                        sy += rowStep;
                    }
                }
            };

            const cardW = 370;
            const rowStep = 38;
            
            const calcDynamicFontSize = (cat, rowStep, maxTotalH) => {
                let fSize = 11;
                while (fSize > 4) {
                    let totalH = 0;
                    let currentRankIndex = 0;
                    let lh = fSize + 3;
                    if (cat.students && cat.students.length > 0) {
                        cat.students.forEach(sGroup => {
                            totalH += Math.max(rowStep, 10 + sGroup.students.length * lh);
                            currentRankIndex++;
                        });
                        totalH += (3 - currentRankIndex) * rowStep;
                    } else {
                        totalH = 3 * rowStep;
                    }
                    if (totalH <= maxTotalH) {
                        return fSize;
                    }
                    fSize -= 0.5;
                }
                return 4;
            };

            const calcHeight = (cat) => {
                let sy = 40 + 16 + 24; // headerH (40) + gap (16) + table header (24)
                const fSizeMed = calcDynamicFontSize(cat, rowStep, 219);
                const lineHeight = fSizeMed + 3;
                let currentRankIndex = 0;
                
                if (cat.students && cat.students.length > 0) {
                    cat.students.forEach(sGroup => {
                        const requiredHeight = Math.max(rowStep, 10 + (sGroup.students.length * lineHeight));
                        sy += requiredHeight;
                        currentRankIndex++;
                    });
                    for (let i = currentRankIndex; i < 3; i++) {
                        sy += rowStep;
                    }
                }
                return sy + 16;
            };

            const h0 = calcHeight(CATEGORIES[0]);
            const h1 = calcHeight(CATEGORIES[1]);
            const cardH = Math.max(h0, h1);
            
            drawCategoryCard(36, 170, cardW, cardH, rowStep, CATEGORIES[0]);
            drawCategoryCard(436, 170, cardW, cardH, rowStep, CATEGORIES[1]);

            // Footer
            const fY = H - 65;
            const cbX = W / 2 - 180, cbY = fY - 35, cbW = 360, cbH = 15;
            
            doc.setFillColor('#0d1b20');
            doc.setDrawColor(GOLD);
            doc.setLineWidth(0.8);
            doc.roundedRect(cbX - 5, cbY, cbW + 10, cbH + 3, 2, 2, 'FD');

            doc.setFontSize(8.5);
            doc.setTextColor(GOLD);
            doc.text('Congratulations to Our Merit Champions!', W / 2, cbY + 11, { align: 'center' });

            doc.setFontSize(8);
            doc.setTextColor('#ffffff');
            doc.text("Your dedication and hard work inspire every student to strive for excellence.", W / 2, fY - 3, { align: 'center' });

            doc.setFillColor(GOLD);
            doc.rect(36, fY, W - 72, 0.8, 'F');

            const curDateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(TEXT_MUTED);
            doc.text(`Generated on ${curDateStr}  •  Countingwell Educational Data Systems`, W / 2, fY + 20, { align: 'center' });

            doc.setFont('helvetica', 'bold');
            doc.setTextColor(PORTAL_TEAL);
            doc.text('countingwell.in', W / 2, fY + 34, { align: 'center' });
        });
    }

    onSelectBoard = (option) => {
        if (option && option.value) {
            this.setState({ board: option.value }, () => {
                this.fetchReportData();
            });
        }
    }

    render() {
        const { currentMonthDate, reportData, isLoading, isPdfGenerating, boardsDropdownItems, board } = this.state;
        const today = new Date();
        const isCurrentMonth = currentMonthDate.getFullYear() === today.getFullYear() && currentMonthDate.getMonth() === today.getMonth();

        return (
            <div className="report-container mt-4 pb-4">
                {boardsDropdownItems.length > 1 && (
                    <div className="row mb-4">
                        <div className="col-lg-3 col-md-6 col-sm-12">
                            <div className="filter-input-label">
                                Board<span className='text-danger'>*</span>
                            </div>
                            <Dropdown
                                placeholder='Select a board'
                                options={boardsDropdownItems}
                                onSelect={this.onSelectBoard}
                                text={"title"}
                                value={board}
                            />
                        </div>
                    </div>
                )}
                
                <div className="report-card mt-2">
                    <div className="report-header-bar">
                        <div className="header-info">
                            <h3 className="report-header-title">School Merit Summary</h3>
                        </div>
                        <div className="header-actions">
                            <MonthNavigator
                                currentMonthDate={currentMonthDate}
                                onMonthChange={this.handleMonthChange}
                                isCurrentMonth={isCurrentMonth}
                            />
                            <button 
                                className="download-pdf-btn" 
                                onClick={this.downloadPDF}
                                disabled={isPdfGenerating || isLoading || reportData.length === 0}
                            >
                                <img src={getImageSource(ImageConstants.EXPORT_TO_EXCEL)} alt="PDF" />
                                {isPdfGenerating ? 'Generating...' : 'Download PDF'}
                            </button>
                        </div>
                    </div>

                    <div className="report-body">
                        {isLoading ? (
                            <div className="text-center my-5">
                                <div className="spinner-border text-info" role="status"></div>
                                <div className="mt-2 text-muted">Loading school summary...</div>
                            </div>
                        ) : reportData.length === 0 ? (
                            <div className="text-center my-5 text-muted">No data available for the selected month.</div>
                        ) : (
                            <div className="report-tables">
                        {reportData.map((data, index) => (
                            <div key={index} className="class-section-report mb-5 bg-white rounded shadow-sm border p-4">
                                <h5 className="font-weight-bold mb-4 class-title">Class {data.className.replace(/\D/g, '') || data.className}</h5>
                                {data.sections.map((sec, secIdx) => {
                                    const tpList = sec.topPerformers || [];
                                    const sllList = sec.selfLearningLeaders || [];
                                    const tpRows = tpList.map((tpGroup, i) => (
                                        <tr key={i}>
                                            <td className="align-middle">
                                                <div className="font-weight-bold text-dark d-flex align-items-center">
                                                    {tpGroup.rank ? <span className="rank-badge-tp">{tpGroup.rank}</span> : null}
                                                    <div>
                                                        {tpGroup.students.map((s, idx) => (
                                                            <div key={idx}>{s.name || "Unknown"}</div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="align-middle text-right merits-tp">
                                                {tpGroup.students.map((s, idx) => (
                                                    <div key={idx}>{Number(s.merits).toFixed(2)}</div>
                                                ))}
                                            </td>
                                        </tr>
                                    ));

                                    const sllRows = sllList.map((sllGroup, i) => (
                                        <tr key={i}>
                                            <td className="align-middle">
                                                <div className="font-weight-bold text-dark d-flex align-items-center">
                                                    {sllGroup.rank ? <span className="rank-badge-sll">{sllGroup.rank}</span> : null}
                                                    <div>
                                                        {sllGroup.students.map((s, idx) => (
                                                            <div key={idx}>{s.name || "Unknown"}</div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="align-middle text-right merits-sll">
                                                {sllGroup.students.map((s, idx) => (
                                                    <div key={idx}>{Number(s.merits).toFixed(2)}</div>
                                                ))}
                                            </td>
                                        </tr>
                                    ));

                                    return (
                                        <div key={sec.sectionId || secIdx} className="section-table-wrapper mb-4">
                                            {(data.sections.length > 1 || sec.sectionName !== 'All') && (
                                                <h6 className="font-weight-bold mb-3 section-title">Section: {sec.sectionName}</h6>
                                            )}
                                            <div className="row">
                                                <div className="col-md-6 mb-3 mb-md-0">
                                                    <div className="table-responsive rounded border h-100">
                                                        <table className="table table-hover mb-0 h-100">
                                                            <thead>
                                                                <tr>
                                                                    <th className="text-muted">Top Performer</th>
                                                                    <th className="text-muted text-right">Merits</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {tpRows.length > 0 ? tpRows : (
                                                                    <tr><td colSpan="2" className="text-center text-muted py-3">No Top Performers</td></tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="table-responsive rounded border h-100">
                                                        <table className="table table-hover mb-0 h-100">
                                                            <thead>
                                                                <tr>
                                                                    <th className="text-muted">Self Learning Leader</th>
                                                                    <th className="text-muted text-right">Merits</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {sllRows.length > 0 ? sllRows : (
                                                                    <tr><td colSpan="2" className="text-center text-muted py-3">No Self Learning Leaders</td></tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    </div>
        );
    }
}
